import {DateTime} from 'luxon';
import {cloneDeep} from 'lodash';
import {isNullOrUndefined} from 'common/util/HelperUtil';

export class DateUtil {
    private static readonly PERIOD_MAX_DATES: number = 90;
    private static readonly WEEK_DAYS: {[id: number]: string} = {1: 'lundi', 2: 'mardi', 3: 'mercredi', 4: 'jeudi', 5: 'vendredi', 6: 'samedi', 7: 'dimanche'};

    public static getCycleDateFromFullDate(date: string, cycle: 'daily' | 'weekly' | 'monthly'): string {
        if (cycle === 'weekly') {
            const result: string = DateTime.fromISO(date.replace(/-W([0-9])$/i, '-W0$1')).toISOWeekDate();
            if (result === null) {
                return date;
            }

            const parts: string[] = result.split('-');

            return parts.length > 2 ? parts[0] + '-' + parts[1] : parts.join('-');
        } else if (cycle === 'monthly') {
            return DateTime.fromISO(date).toFormat('yyyy-LL');
        }

        return DateTime.fromISO(date).toISODate();
    }

    public static displayWeekDay(weekDay: number): string {
        let displayWeekDay: string = '';
        if (DateUtil.WEEK_DAYS[weekDay] !== undefined) {
            displayWeekDay = DateUtil.WEEK_DAYS[weekDay];
        }

        return displayWeekDay;
    }

    public static generatePeriod(fromDate: string, toDate: string, initialValue: Record<string, any>, addDate: boolean = true, MAX_DAYS: number = DateUtil.PERIOD_MAX_DATES,
        cycle: 'daily' | 'weekly' | 'monthly' = 'daily'): {[date: string]: Record<string, any>} {
        const jump: {[str: string]: number} = {'daily': {day: 1}, 'weekly': {week: 1}, 'monthly': {month: 1}}[cycle];
        let cursor: string = DateTime.fromISO(fromDate, {setZone: true}).toISODate();
        const stop: string = DateTime.fromISO(toDate, {setZone: true}).toISODate();
        let i: number = 0;
        const data: {[date: string]: Record<string, any>} = {};
        while (cursor <= stop && i < MAX_DAYS) {
            const date: string = DateUtil.getCycleDateFromFullDate(cursor, cycle);
            data[date] = cloneDeep(initialValue);
            if (addDate) {
                data[date].date = date;
            }
            cursor = DateTime.fromISO(cursor, {setZone: true}).plus(jump).toISODate();
            i++;
        }

        return data;
    }

    public static getOpenPeriods(openings: {from: string, to: string}[], closings: {from: string, to: string}[]): {from: string, to: string}[] {
        const results: {from: string, to: string}[] = [];

        openings.forEach((opening) => {
            const tmpOpenings: {from: string, to: string}[] = [opening];
            while (tmpOpenings.length > 0) {
                const open: {from: string, to: string} = tmpOpenings[0];

                const isOpen: boolean = closings.find((closing) => closing.from <= open.from && open.to <= closing.to) === undefined;
                if (isOpen) {
                    const startLater: {from: string, to: string} = closings.find((closing) => closing.from <= open.from && open.from < closing.to && closing.to < open.to);
                    if (startLater !== undefined) {
                        tmpOpenings.push({from: startLater.to, to: open.to});
                    } else {
                        const endEarlier: {from: string, to: string} = closings.find((closing) => open.from < closing.from && open.to > closing.from && open.to <= closing.to);
                        if (endEarlier !== undefined) {
                            tmpOpenings.push({from: open.from, to: endEarlier.from});
                        } else {
                            const cut: {from: string, to: string} = closings.find((closing) => open.from < closing.from && closing.to < open.to);
                            if (cut !== undefined) {
                                tmpOpenings.push({from: open.from, to: cut.from});
                                tmpOpenings.push({from: cut.to, to: open.to});
                            } else {
                                results.push(open);
                            }
                        }
                    }
                }

                tmpOpenings.shift();
            }
        });


        return results;
    }

    public static limitDate(targetDate: string, baseDate: string, limitInSeconds: number = 0): string {
        if (targetDate === undefined || baseDate === undefined) {
            return undefined;
        }

        const target: string = DateTime.fromISO(targetDate, {setZone: true}).toISO();
        const limit: string = DateTime.fromISO(baseDate, {setZone: true}).plus({seconds: limitInSeconds}).toISO();

        return target > limit ? limit : target;
    }

    public static localeToISO(localeDate: string): string {
        const frFormat1: RegExp = new RegExp('[0-9]{2}/[0-9]{2}/[0-9]{4}');
        const frFormat2: RegExp = new RegExp('[0-9]{2}-[0-9]{2}-[0-9]{4}');
        const isoFormat: RegExp = new RegExp('[0-9]{4}-[0-9]{2}-[0-9]{2}');

        let isoDate: string;
        if (frFormat1.test(localeDate)) {
            const parts: string[] = localeDate.split('/');
            isoDate = parts[2] + '-' + parts[1] + '-' + parts[0];
        } else if (frFormat2.test(localeDate)) {
            const parts: string[] = localeDate.split('-');
            isoDate = parts[2] + '-' + parts[1] + '-' + parts[0];
        } else if (isoFormat.test(localeDate)) {
            isoDate = localeDate;
        }

        return isoDate;
    }

    public static slotToTime(timeSlot: string): string {
        const timeFormat: RegExp = new RegExp('^(\\s)?[0-9]{2}:[0-9]{2}(\\s)?$');
        const slotFormat: RegExp = new RegExp('^(\\s)?[0-9]{2}:[0-9]{2}(\\s)?-(\\s)?[0-9]{2}:[0-9]{2}(\\s)?$');

        timeSlot = timeSlot.trim();

        let time: string;
        if (timeFormat.test(timeSlot)) {
            time = timeSlot.trim() + ':00';
        } else if (slotFormat.test(timeSlot)) {
            const parts: string[] = timeSlot.split('-');
            time = parts[0].trim() + ':00';
        }

        return time;
    }

    public static delay(fromDate: string, toDate: string, unit: 'minutes' | 'hours' | 'days' | 'months'): number {
        if (isNullOrUndefined(fromDate) || isNullOrUndefined(toDate)) {
            return 0;
        }

        return Math.round(Math.abs(DateTime.fromISO(fromDate, {setZone: true}).toUTC()
            .diff(DateTime.fromISO(toDate, {setZone: true}).toUTC(), [unit])[unit]))
    }

    public static round(date: string, roundingInMs: number = 0): string {
        if (typeof date !== 'string') {
            return date;
        }

        const dateTime: DateTime = DateTime.fromISO(date, {setZone: true});

        return DateTime.fromMillis(Math.round((+dateTime.toMillis()) / roundingInMs) * roundingInMs).setZone(dateTime.zone).toISO();
    }

    public static min(dates: string[]): string {
        return dates.reduce((min, date) => {
            if (typeof min !== 'string' || min > date) {
                return date;
            }

            return min;
        }, undefined);
    }

    public static max(dates: string[]): string {
        return dates.reduce((max, date) => {
            if (max === undefined || max < date) {
                return date;
            }

            return max;
        }, undefined);
    }
}
